// src/lib/api.ts
import { baseURL, baseURL1 } from "../../../Redux/baseURL";

const API_URL = `${baseURL}/api/career-coach`;
const MAIN_APP_URL = `${baseURL1}`;
const LOGIN_URL = `${baseURL1}/login`;

// Token Management
export const getToken = () => {
  // Otherwise get from localStorage
  return localStorage.getItem("token");
};

export const removeToken = () => {
  localStorage.removeItem("token");
};

export const getUserFromToken = () => {
  const token = getToken();
  if (!token) return null;

  try {
    return token;
  } catch {
    return null;
  }
};

export const getUserId = () => {
  const user = getUserFromToken();
  return user?.userId || null;
};

// Redirects
export const redirectToLogin = () => {
  removeToken();
  window.location.href = LOGIN_URL;
};

export const redirectToMainApp = () => {
  window.location.href = MAIN_APP_URL;
};

// API Request Helper
const apiRequest = async (
  endpoint,
  options = {}
) => {
  const token = getToken();

  if (!token) {
    redirectToLogin();
    return { success: false, error: "No authentication token" };
  }

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        // Authorization: `Bearer ${token}`,
        ...options.headers,
      },
      credentials: "include",
    });

    if (response.status === 401) {
      redirectToLogin();
      return { success: false, error: "Unauthorized" };
    }

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.message || "Request failed" };
    }

    return { data: data.data };
  } catch (error) {
    console.error("API request error:", error);
    return { success: false, error: "Network error" };
  }
};

// Profile APIs
export const ensureProfile = async () => {
  return apiRequest("/profile/ensure", { method: "POST" });
};

export const getProfile = async () => {
  return apiRequest("/profile");
};

export const updateProfile = async (data) => {
  return apiRequest("/profile", {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

// Resume APIs
export const getLatestResume = async () => {
  return apiRequest("/resumes");
};

export const getResumes = async () => {
  return apiRequest("/resumes");
};

export const createResume = async (
  data
) => {
  return apiRequest("/resumes", {
    method: "POST",
    body: JSON.stringify({
      file_name: data.file_name,
      parsed_data: data.parsed_data,
    }),
  });
};

export const deleteResume = async (
  resumeId
) => {
  return apiRequest(`/resumes/${resumeId}`, { method: "DELETE" });
};

// Session APIs
export const getSessions = async () => {
  return apiRequest("/sessions");
};

export const getSession = async (
  sessionId
) => {
  return apiRequest(`/sessions/${sessionId}`);
};

export const createSession = async (
  sessionType = "general"
) => {
  return apiRequest("/sessions", {
    method: "POST",
    body: JSON.stringify({ session_type: sessionType }),
  });
};

export const updateSession = async (
  sessionId,
  data
) => {
  return apiRequest(`/sessions/${sessionId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

export const deleteSession = async (
  sessionId
) => {
  return apiRequest(`/sessions/${sessionId}`, { method: "DELETE" });
};

// Message APIs
export const getMessages = async (
  sessionId
) => {
  return apiRequest(`/sessions/${sessionId}/messages`);
};

export const createMessage = async (
  sessionId,
  role,
  content
) => {
  return apiRequest(`/sessions/${sessionId}/messages`, {
    method: "POST",
    body: JSON.stringify({ role, content }),
  });
};

// Deliverable APIs
export const getDeliverables = async () => {
  return apiRequest("/deliverables");
};

export const getSessionDeliverables = async (
  sessionId
) => {
  return apiRequest(`/sessions/${sessionId}/deliverables`);
};

export const createDeliverable = async (data) => {
  return apiRequest("/deliverables", {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const deleteDeliverable = async (
  deliverableId
) => {
  return apiRequest(`/deliverables/${deliverableId}`, { method: "DELETE" });
};

// Goal APIs
export const getGoals = async () => {
  return apiRequest("/goals");
};

export const createGoal = async (data) => {
  return apiRequest("/goals", {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const updateGoal = async (
  goalId,
  data
) => {
  return apiRequest(`/goals/${goalId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

export const deleteGoal = async (goalId) => {
  return apiRequest(`/goals/${goalId}`, { method: "DELETE" });
};

// Export all as default object
const api = {
  getToken,
  removeToken,
  getUserFromToken,
  getUserId,
  redirectToLogin,
  redirectToMainApp,
  ensureProfile,
  getProfile,
  updateProfile,
  getLatestResume,
  getResumes,
  createResume,
  deleteResume,
  getSessions,
  getSession,
  createSession,
  updateSession,
  deleteSession,
  getMessages,
  createMessage,
  getDeliverables,
  getSessionDeliverables,
  createDeliverable,
  deleteDeliverable,
  getGoals,
  createGoal,
  updateGoal,
  deleteGoal,
};

export default api;
